import { TFunction } from "i18next";
import { z } from "zod";

import { documentsUploadSchema } from "src/core/forms/folder/documentsUpload.schema";
import { FR_SIREN_REGEX } from "src/core/forms/validators/regex";
import { FolderFileQuery } from "src/core/gql/graphql";
import { getFolderFieldValue } from "src/core/mappers/folder.mapper";
import { FolderEntrepriseFormValues } from "src/core/types/folder";

type Props = {
  t: TFunction;
};

export const entrepriseSchema = ({ t }: Props) => {
  return z
    .object({
      raisonSociale: z.string().min(1, { message: t("global.form.error.mandatoryField") }),
      siren: z.string().optional(),
      activite: z.string().optional(),
      paysImmatriculation: z.string().optional(),
      typeJuridique: z.string().optional(),
      formeJuridique: z.string().optional(),
      formeJuridiquePrecision: z.string().optional(),
      documents: documentsUploadSchema(t),
      codePostal: z.string().optional(),
      adresse: z.string().optional(),
      pays: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.paysImmatriculation === "FR" && data.siren) {
        if (!FR_SIREN_REGEX.test(data.siren)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form.entreprise.error.sirenInvalid"),
            path: ["siren"],
          });
        }
      }
    });
};

export type EntrepriseFormValues = z.infer<ReturnType<typeof entrepriseSchema>>;

/**
 * Construit les valeurs par défaut du formulaire Entreprise à partir du folderFile
 */
export const defaultEntrepriseFormValues = (
  folderFile: FolderFileQuery
): FolderEntrepriseFormValues => {
  return {
    raisonSociale: getFolderFieldValue(folderFile, "name"),
    paysImmatriculation: getFolderFieldValue(folderFile, "registrationCountryCode"),
    activite: getFolderFieldValue(folderFile, "activityCode"),
    siren: getFolderFieldValue(folderFile, "registration"),
    typeJuridique: getFolderFieldValue(folderFile, "entityTypeCode", "unlisted"),
    formeJuridique: getFolderFieldValue(folderFile, "customListCode2"),
    formeJuridiquePrecision: getFolderFieldValue(folderFile, "customString3"),
    documents: {}, // reste géré dans le useEffect
    codePostal: getFolderFieldValue(folderFile, "customString1"),
    adresse: getFolderFieldValue(folderFile, "customString2"),
    pays: getFolderFieldValue(folderFile, "customCountryCode1"),
  };
};
